import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { VIEW_TYPE } from '@constants/surveys';
import { alertActions, channelsActions, agentsActions } from '@actions';
import { agentsService } from '@services';
import { useTranslation, useYupValidationResolver, useChannels } from '@hooks';
import { AgentStatus } from '@constants/agents';
import General from './Form/GeneralFields';
import Handoff, { HandoffMap } from './Form/Handoff';
import Survey from './Form/Survey';
import AutoClose, { AutoCloseEnum } from './Form/AutoClose';
import PulseAIPlayground from './PulseAIPlayground';
import Instructions from './Form/Instructions';
import { Modal, Content, Form } from './styles';

const EditModal = ({ modal, onClose, isSuperAdmin, selectedAgentRef, brands, onDelete }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { channelIsAssignedToBrandOrLocation } = useChannels();
    const isMobile = useMediaQuery('@media (max-width: 768px)');

    const SurveysSchema = yup.object().shape({
        name: yup.string().required('validation.required'),
        locationId: yup.string().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) =>
                locationType === LOCATION_TYPE.LOCATION || (locationType === LOCATION_TYPE.BRAND && !brandAccess),
            then: yup.string().required('validation.required'),
            otherwise: yup.string().nullable(),
        }),
        brandId: yup.string().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) => locationType === LOCATION_TYPE.BRAND && brandAccess,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().nullable(),
        }),
        channels: yup.array().min(1, 'validation.required'),
        handoffResponseTime: yup.number().when('handoff', {
            is: handoff => handoff === `${HandoffMap.Enabled}`,
            then: yup.number().required('validation.required'),
            otherwise: yup.number().notRequired(),
        }),
        surveyTemplateId: yup.string().when('surveyEnabled', {
            is: surveyEnabled => surveyEnabled,
            then: yup.string().nullable().required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        closeTimeout: yup.string().when('autoClose', {
            is: value => value === AutoCloseEnum.Close,
            then: yup.string().test('closeTimeoutFields', 'validation.required', function () {
                const { daysClose, hoursClose, minutesClose } = this.parent;
                return daysClose > 0 || hoursClose > 0 || minutesClose > 0;
            }),
            otherwise: yup.string().notRequired(),
        }),
        unassignTimeout: yup.string().when('autoClose', {
            is: value => value === AutoCloseEnum.Unassign,
            then: yup.string().test('unassignTimeoutFields', 'validation.required', function () {
                const { daysUnassign, hoursUnassign, minutesUnassign } = this.parent;
                return daysUnassign > 0 || hoursUnassign > 0 || minutesUnassign > 0;
            }),
            otherwise: yup.string().notRequired(),
        }),
        customInstructions: yup.string().max(1000, 'validation.maxLength'),
    });

    const resolver = useYupValidationResolver(SurveysSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        trigger,
        clearErrors,
        handleSubmit,
        formState: { isValid, isDirty, errors, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            name: '',
            locationId: '',
            brandId: '',
            locationType: LOCATION_TYPE.BRAND,
            brandAccess: false,
            channels: [],
            status: !!AgentStatus.Disabled,
            contactEmailEnabled: false,
            contactEmailRequired: false,
            contactPhoneNumberEnabled: false,
            contactPhoneNumberRequired: false,
            contactNameEnabled: false,
            contactNameRequired: false,
            autoClose: AutoCloseEnum.DoNothing,
            closeTimeout: '',
            unassignTimeout: '',
            daysClose: '00',
            hoursClose: '00',
            minutesClose: '00',
            daysUnassign: '00',
            hoursUnassign: '00',
            minutesUnassign: '00',
            handoff: `${HandoffMap.Enabled}`,
            handoffResponseTime: 0,
            surveyEnabled: false,
            surveyTemplateId: '',
            customInstructions: '',
        },
    });

    useEffect(() => {
        if (selectedAgentRef.current && modal.open) {
            setValue('name', selectedAgentRef.current.name, { shouldValidate: true });
            const filteredChannels = selectedAgentRef.current?.channels.filter(channel =>
                channelIsAssignedToBrandOrLocation(
                    channel,
                    selectedAgentRef.current?.brandId,
                    selectedAgentRef.current?.locationId,
                ),
            );
            setValue('channels', filteredChannels, { shouldValidate: true });
            setValue('status', !!selectedAgentRef.current.status);
            setValue('contactEmailEnabled', selectedAgentRef.current.contactEmailEnabled);
            setValue('contactEmailRequired', selectedAgentRef.current.contactEmailRequired);
            setValue('contactPhoneNumberEnabled', selectedAgentRef.current.contactPhoneNumberEnabled);
            setValue('contactPhoneNumberRequired', selectedAgentRef.current.contactPhoneNumberRequired);
            setValue('contactNameEnabled', selectedAgentRef.current.contactNameEnabled);
            setValue('contactNameRequired', selectedAgentRef.current.contactNameRequired);
            setValue('autoClose', `${selectedAgentRef.current.autoClose}`, { shouldValidate: true });
            if (selectedAgentRef.current.closeTimeout) {
                const [daysClose, hoursClose, minutesClose] = selectedAgentRef.current.closeTimeout.split(':');
                setValue('daysClose', daysClose);
                setValue('hoursClose', hoursClose);
                setValue('minutesClose', minutesClose);
            }
            if (selectedAgentRef.current.unassignTimeout) {
                const [daysUnassign, hoursUnassign, minutesUnassign] =
                    selectedAgentRef.current.unassignTimeout.split(':');
                setValue('daysUnassign', daysUnassign);
                setValue('hoursUnassign', hoursUnassign);
                setValue('minutesUnassign', minutesUnassign);
            }
            setValue('handoff', `${selectedAgentRef.current.handoff}`, { shouldValidate: true });
            setValue('handoffResponseTime', selectedAgentRef.current.handoffResponseTime, { shouldValidate: true });
            setValue('surveyEnabled', selectedAgentRef.current.surveyEnabled, { shouldValidate: true });
            setValue('surveyTemplateId', selectedAgentRef.current.surveyTemplateId || '', { shouldValidate: true });
            setValue('customInstructions', selectedAgentRef.current.customInstructions || '');
        }
    }, [selectedAgentRef.current, setValue, modal.open]);

    const isView = modal.type === VIEW_TYPE.VIEW;

    const onSubmit = async () => {
        try {
            const {
                daysClose,
                hoursClose,
                minutesClose,
                daysUnassign,
                hoursUnassign,
                minutesUnassign,
                autoClose,
                ...values
            } = getValues();
            await agentsService.createOrUpdateAgent({
                ...selectedAgentRef.current,
                ...values,
                status: Number(values.status),
                locationId: values.locationId || null,
                brandId: values.brandId || null,
                handoff: +values.handoff,
                autoClose: +autoClose,
                closeTimeout: autoClose === AutoCloseEnum.Close ? `${daysClose}:${hoursClose}:${minutesClose}` : null,
                unassignTimeout:
                    autoClose === AutoCloseEnum.Unassign ? `${daysUnassign}:${hoursUnassign}:${minutesUnassign}` : null,
            });
            await dispatch(agentsActions.get());
            await dispatch(channelsActions.get());
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            onClose();
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.editFail')));
        }
    };

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onClose}
            title={selectedAgentRef.current ? t('Agents.editAgent') : t('buttons.addAgent')}
            onPrimaryAction={!isSuperAdmin ? null : handleSubmit(onSubmit)}
            primaryActionText={t('buttons.saveChanges')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
            onAdditionalAction={!isSuperAdmin ? null : onDelete}
            additionalTextButton={t('buttons.delete')}
        >
            <FormProvider {...{ getValues, setValue, control, watch, trigger, errors, clearErrors }}>
                <Content>
                    <Form>
                        <General
                            isView={isView}
                            isEdit={modal.type === VIEW_TYPE.EDIT}
                            brands={brands}
                            selectedAgentRef={selectedAgentRef}
                        />
                        <AutoClose isView={isView} />
                        <Handoff isView={isView} isMobile={isMobile} />
                        <Survey isView={isView} isMobile={isMobile} />
                        <Instructions isView={isView} />
                    </Form>
                    <PulseAIPlayground selectedAgentRef={selectedAgentRef} />
                </Content>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
